import { useState, useRef, useEffect, useCallback } from 'react'

export interface InlineEditProps {
  /** The current display value */
  value: string | number
  /** Field name identifier (used for data attributes and Tab navigation) */
  fieldName: string
  /** Input type to render in edit mode */
  type: 'text' | 'number' | 'date' | 'select'
  /** Options for select type (required when type='select') */
  options?: { label: string; value: string }[]
  /** Callback when value is committed (Enter or blur) */
  onSave: (newValue: string | number) => Promise<void> | void
  /** Optional CSS class for the display cell */
  className?: string
  /** Optional formatter for display value (e.g., adding $ prefix) */
  displayFormatter?: (value: string | number) => string
  /** Whether the cell is editable (default: true) */
  editable?: boolean
}

export function InlineEdit({
  value,
  fieldName,
  type,
  options,
  onSave,
  className = '',
  displayFormatter,
  editable = true,
}: InlineEditProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState<string>(String(value))
  const [hasError, setHasError] = useState(false)
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null)

  // Sync editValue when value prop changes externally
  useEffect(() => {
    if (!isEditing) {
      setEditValue(String(value))
    }
  }, [value, isEditing])

  // Auto-focus and select text when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      if (inputRef.current instanceof HTMLInputElement && type !== 'date') {
        inputRef.current.select()
      }
    }
  }, [isEditing, type])

  const handleClick = () => {
    if (!editable) return
    setIsEditing(true)
    setEditValue(String(value))
    setHasError(false)
  }

  const handleSave = useCallback(async () => {
    // Validate number type
    if (type === 'number') {
      const num = parseFloat(editValue)
      if (isNaN(num)) {
        // Invalid number — revert
        setEditValue(String(value))
        setIsEditing(false)
        return
      }
    }

    // Don't save if value hasn't changed
    const newValue = type === 'number' ? parseFloat(editValue) : editValue
    if (String(newValue) === String(value)) {
      setIsEditing(false)
      return
    }

    try {
      await onSave(newValue)
      setIsEditing(false)
    } catch {
      // Revert on error and flash red border
      setEditValue(String(value))
      setHasError(true)
      setTimeout(() => {
        setHasError(false)
        setIsEditing(false)
      }, 1500)
    }
  }, [editValue, type, value, onSave])

  const handleCancel = () => {
    setEditValue(String(value))
    setIsEditing(false)
    setHasError(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
    // Tab: default browser behavior (blur → triggers save via onBlur)
  }

  // Display mode
  if (!isEditing) {
    const displayValue = displayFormatter ? displayFormatter(value) : String(value) || '—'
    return (
      <span
        onClick={handleClick}
        className={`inline-block w-full cursor-pointer rounded px-1 py-0.5 transition-colors hover:bg-[var(--color-bg-tertiary)] ${className} ${editable ? '' : 'cursor-default'}`}
        data-field={fieldName}
        tabIndex={editable ? 0 : undefined}
        onKeyDown={(e) => {
          if (editable && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            handleClick()
          }
        }}
        role={editable ? 'button' : undefined}
        aria-label={editable ? `Edit ${fieldName}` : undefined}
      >
        {displayValue}
      </span>
    )
  }

  // Edit mode — select
  if (type === 'select' && options) {
    return (
      <select
        ref={inputRef as React.RefObject<HTMLSelectElement>}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className={`w-full px-1 py-0.5 rounded border text-sm bg-white focus:outline-none ${
          hasError
            ? 'border-[var(--color-red)] ring-1 ring-[var(--color-red)]'
            : 'border-[var(--color-accent)] ring-1 ring-[var(--color-accent)]'
        }`}
        data-field={fieldName}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    )
  }

  // Edit mode — text, number, date
  return (
    <input
      ref={inputRef as React.RefObject<HTMLInputElement>}
      type={type}
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      onBlur={handleSave}
      onKeyDown={handleKeyDown}
      step={type === 'number' ? '0.01' : undefined}
      className={`w-full px-1 py-0.5 rounded border text-sm bg-white focus:outline-none ${
        hasError
          ? 'border-[var(--color-red)] ring-1 ring-[var(--color-red)]'
          : 'border-[var(--color-accent)] ring-1 ring-[var(--color-accent)]'
      }`}
      data-field={fieldName}
    />
  )
}
